import React, { useRef, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { useResizeDetector } from 'react-resize-detector';
import styled, { ThemeContext } from 'styled-components';
import type { ComputedInvestmentResidual, Fund, OperationResult, Portfolio } from 'venn-api';
import { getBatchInvestmentResiduals, getFund, getInvestmentResidual, getSpecificPortfolioV3 } from 'venn-api';
import { Button, TablePlaceholder } from 'venn-ui-kit';
import type { AnalysisSubject } from 'venn-utils';
import {
  createGetFundKey,
  invalidateInvestment,
  invalidateInvestments,
  logExceptionIntoSentry,
  logExhaustive,
  recursiveGetFunds,
  useFetchApi,
  useHasFF,
  useQueries,
  useStableQueriesData,
} from 'venn-utils';
import { forceZeroResidualForecastChangedAtom } from '../../../venn-state/src';
import EmptyState from '../empty-state/EmptyState';
import type { UseRangeAnalysisReturn } from '../hooks/useRangeAnalysis';
import { PlaceholderWrapper } from '../manage-data';
import { getBulkManagementColumns } from './columns';
import { getBulkManagementData } from './deprecated-columns';
import type { BulkManageRow } from './types';
import { BulkManageAction } from './types';
import { BulkProxySection } from './BulkProxySection';
import { BulkDataGrid } from './BulkDataGrid';
import { PortfoliosContext } from '../contexts';
import type { SelectionChangedEvent } from 'ag-grid-community';
import type { QueryObserverOptions } from '@tanstack/react-query';
import { compact } from 'lodash';
import type { AgGridReact } from 'ag-grid-react';

interface BulkManagementTableProps {
  subject: AnalysisSubject;
  useRangeAnalysisReturn: UseRangeAnalysisReturn;
  onFundUpdated?: (fundId: string) => void;
  canEditForecasts: boolean;
  canEditProxies: boolean;
}

export const BulkManagementTable: React.FC<React.PropsWithChildren<BulkManagementTableProps>> = ({
  subject,
  useRangeAnalysisReturn,
  onFundUpdated,
  canEditForecasts,
  canEditProxies,
}) => {
  const gridRef = useRef<AgGridReact | null>(null);

  const { ref, width } = useResizeDetector<HTMLDivElement>({ handleWidth: true });
  const { rangeAnalysis, refresh: refreshRange, analysisRequest: rangeAnalysisRequest } = useRangeAnalysisReturn;
  const fundIds = useMemo(() => {
    const allFunds = new Set(recursiveGetFunds(subject.portfolio));
    if (subject.activeBenchmarkType === 'investment' && typeof subject.activeBenchmarkId === 'string') {
      allFunds.add(subject.activeBenchmarkId);
    }
    if (subject.categoryGroup) {
      allFunds.add(subject.categoryGroup.categoryId);
    }
    return [...allFunds];
  }, [subject.portfolio, subject.activeBenchmarkId, subject.activeBenchmarkType, subject.categoryGroup]);

  const fundQueries = useQueries({
    queries: fundIds.map(
      (id): QueryObserverOptions<Fund> => ({
        staleTime: 60 * 1000,
        queryKey: createGetFundKey(id),
        queryFn: async () => {
          try {
            const response = await getFund(id);
            return response.content;
          } catch (e) {
            logExceptionIntoSentry(e);
            throw e;
          }
        },
      }),
    ),
  });
  const fundsLoading = fundQueries.some((query) => query.isLoading);
  const maybeFundsData = useStableQueriesData(fundQueries);
  const fundsData = useMemo(() => compact(maybeFundsData), [maybeFundsData]);

  const hasFullHistory = useHasFF('extend_full_history_ff');
  const theme = useContext(ThemeContext);

  const { masterPortfolio } = useContext(PortfoliosContext);
  const secondaryPortfolio = subject.secondaryPortfolioComparisonType === 'MASTER' ? masterPortfolio : undefined;
  const { result: benchmarkPortfolio } = useFetchApi(fetchBenchmarkPortfolio, subject.activeBenchmarkId);

  /* Residual forecast lifecycle is distinct from that of the fund object as the data has to be fetched separately */
  const [residualForecasts, setResidualForecasts] = useState<{ [key: string]: ComputedInvestmentResidual } | undefined>(
    undefined,
  );
  const fundIdsForResidualForecasts = useMemo(
    () => (subject.fund?.id ? [...fundIds, subject.fund?.id] : fundIds),
    [fundIds, subject.fund],
  );
  const { result: fetchedResidualForecasts, refresh: refreshResidualForecasts } = useFetchApi(
    getBatchInvestmentResiduals,
    fundIdsForResidualForecasts,
  );
  useLayoutEffect(() => {
    setResidualForecasts(fetchedResidualForecasts);
  }, [fetchedResidualForecasts]);
  const forceZeroResidualForecastChanged = useRecoilValue(forceZeroResidualForecastChangedAtom);

  useEffect(() => {
    refreshResidualForecasts();
  }, [forceZeroResidualForecastChanged, refreshResidualForecasts]);

  /* Re-sync the residual data for a single investment. Do not override all others. */
  const refreshSingleForecastResidual = useCallback(
    async (id: string) => {
      const newResidual = (await getInvestmentResidual(id)).content;

      const updatedResidualForecasts = {
        ...residualForecasts,
        [id]: newResidual,
      };
      setResidualForecasts(updatedResidualForecasts);
    },
    [residualForecasts],
  );

  const rowData = useMemo(
    () =>
      getBulkManagementData(
        subject,
        fundsData,
        rangeAnalysis,
        rangeAnalysisRequest,
        secondaryPortfolio,
        benchmarkPortfolio,
        residualForecasts,
      ),
    [
      subject,
      fundsData,
      rangeAnalysis,
      rangeAnalysisRequest,
      secondaryPortfolio,
      benchmarkPortfolio,
      residualForecasts,
    ],
  );

  const handleRowUpdate = useCallback(
    async (action: BulkManageAction, row: BulkManageRow, investmentId?: string) => {
      switch (action) {
        case BulkManageAction.FUND_MODIFIED:
          await refreshRange();
          if (row.investmentId) {
            onFundUpdated?.(row.investmentId);
            await invalidateInvestment(row.investmentId);
          }
          break;
        case BulkManageAction.INVESTMENT_FORECAST_MODIFIED:
          if (row.investmentId) {
            await refreshSingleForecastResidual(investmentId ?? row.investmentId);
          }
          break;
        default:
          logExhaustive(action);
      }
    },
    [refreshRange, onFundUpdated, refreshSingleForecastResidual],
  );

  const [selectedFundIds, setSelectedFundIds] = useState<string[]>([]);

  const columns = useMemo(
    () =>
      getBulkManagementColumns(
        rangeAnalysis,
        handleRowUpdate,
        theme,
        canEditForecasts,
        canEditProxies,
        hasFullHistory,
        width,
        rangeAnalysisRequest,
      ),
    [
      rangeAnalysis,
      handleRowUpdate,
      theme,
      canEditForecasts,
      canEditProxies,
      hasFullHistory,
      width,
      rangeAnalysisRequest,
    ],
  );

  const onBulkProxyChange = useCallback(async (fundIds?: string[]) => {
    if (!fundIds) return;
    await invalidateInvestments(fundIds);
  }, []);

  const onClearSelected = useCallback(() => {
    setSelectedFundIds([]);
    gridRef.current?.api.deselectAll();
  }, []);

  const onSelectionChanged = useCallback((event: SelectionChangedEvent) => {
    setSelectedFundIds(event.api.getSelectedRows().map((row) => row.investmentId));
  }, []);

  if (fundIds.length === 0 && !subject.fund) {
    return <EmptyState header="There are no investments in this portfolio." />;
  }

  if (fundsLoading) {
    return (
      <PlaceholderWrapper>
        <TablePlaceholder />
      </PlaceholderWrapper>
    );
  }

  return (
    <Wrapper ref={ref}>
      <BulkDataGrid gridRef={gridRef} rowData={rowData} columnDefs={columns} onSelectionChanged={onSelectionChanged} />
      {selectedFundIds.length > 0 && (
        <>
          <Button onClick={onClearSelected}>clear selected</Button>
          <BulkProxySection selectedFundIds={selectedFundIds} funds={fundsData} onProxyChange={onBulkProxyChange} />
        </>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 100%;
  position: relative;

  .bulk-management-table-wrapper,
  & > div {
    width: 100%;
  }
`;

const fetchBenchmarkPortfolio = async (
  benchmarkId?: string | number,
): Promise<OperationResult<Portfolio | undefined>> => {
  // if it's not a portfolio id, just return a response with undefined content
  if (typeof benchmarkId !== 'number') {
    return {
      status: 200,
      headers: {},
      content: undefined,
    };
  }
  return getSpecificPortfolioV3(benchmarkId);
};
